// types
import { ChartData } from 'utils/charts/rows'
import {
  ChartSide,
  ChartType,
  ChartStack,
  ChartConfig,
  ChartDisplay,
  ChartPalette,
  ChartLineStep,
  ChartPieVariant,
  ChartLabelFormat,
  ChartNumberFormat,
  ChartNameLocation,
  ChartSelectedMode,
  ChartLabelInterval,
  ChartLabelPosition,
  ChartTooltipTrigger
} from 'types/charts'
// constants
import { CHART_PALETTES } from 'constants/charts'
// utils
import { escape, sumBy } from 'lodash'

const COMPACT_UNITS = [
  { value: 1e12, suffix: 'T' },
  { value: 1e9, suffix: 'B' },
  { value: 1e6, suffix: 'M' },
  { value: 1e3, suffix: 'K' }
]

export const formatChartNumber = (value: number | null | undefined, format: ChartNumberFormat, isPercent = false) => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '–'
  }

  if (isPercent) {
    return `${value.toFixed(Math.max(format.decimals, 1))}%`
  }

  const unit = format.compact ? COMPACT_UNITS.find((item) => Math.abs(value) >= item.value) : undefined
  const number = unit ? value / unit.value : value
  const text = number.toLocaleString(undefined, {
    minimumFractionDigits: unit ? 0 : format.decimals,
    maximumFractionDigits: unit ? Math.max(format.decimals, 1) : format.decimals
  })

  return `${format.prefix || ''}${text}${unit?.suffix || ''}${format.suffix || ''}`
}

const truncate = (text: string, length: number) =>
  length && text.length > length ? `${text.slice(0, length)}...` : text

export const getChartPalette = (colors: ChartDisplay['colors']) =>
  colors.palette === ChartPalette.Custom && colors.custom?.length
    ? colors.custom
    : CHART_PALETTES[colors.palette] || CHART_PALETTES[ChartPalette.Default]

const getLegendPosition = (legend: ChartDisplay['legend']) => {
  const isVertical = legend.position === ChartSide.Left || legend.position === ChartSide.Right

  return {
    orient: isVertical ? 'vertical' : 'horizontal',
    [legend.position]: 8,
    ...(isVertical ? { top: 'middle' } : { left: 'center' })
  }
}

// Room the legend takes from the plot, so the axes never run under it
const getLegendMargin = (display: ChartDisplay, isLegendShown: boolean) => {
  const margin = { top: 0, right: 0, bottom: 0, left: 0 }

  if (!isLegendShown) {
    return margin
  }

  const isHorizontal = display.legend.position === ChartSide.Top || display.legend.position === ChartSide.Bottom

  margin[display.legend.position] = isHorizontal ? 32 : 120
  return margin
}

// Room the title takes above the plot
const getTitleMargin = (display: ChartDisplay) => (display.title.show ? (display.title.subtext ? 48 : 28) : 0)

// ECharts' roseType of a rose variant
const ECHARTS_ROSE_TYPES = {
  [ChartPieVariant.RoseRadius]: 'radius',
  [ChartPieVariant.RoseArea]: 'area'
}

// A pie's free gap to the title and the legend, so its outside labels do not touch them
const PIE_GAP = 12

// isShare: the values drawn are shares already (a bar stacked to 100%), so the percent is the value itself, its share
// of the category, the one the bar shows
const getLabelFormatter = (display: ChartDisplay, data: ChartData, isShare = false) => {
  const totals = data.series.map((serie) => sumBy(serie.values, (value) => value || 0))

  const getShare = (value: number | null, seriesIndex: number) => {
    if (isShare) {
      return value || 0
    }

    return totals[seriesIndex] ? ((value || 0) / totals[seriesIndex]) * 100 : 0
  }

  return (params: any) => {
    const value = Array.isArray(params.value) ? params.value[1] : params.value
    const share = params.percent ?? getShare(value, params.seriesIndex)
    const valueText = formatChartNumber(value, display.number, data.isPercent)
    const shareText = `${share.toFixed(display.number.decimals)}%`

    switch (display.labels.format) {
      case ChartLabelFormat.Percent:
        return shareText
      case ChartLabelFormat.Name:
        return params.name
      case ChartLabelFormat.NameValue:
        return `${params.name}: ${valueText}`
      case ChartLabelFormat.NamePercent:
        return `${params.name}: ${shareText}`
      default:
        return valueText
    }
  }
}

// "top" of a horizontal bar is its end, on the right
const getAxisLabelPosition = (position: ChartDisplay['labels']['position'], isHorizontal: boolean) => {
  const outer = position === ChartLabelPosition.Outside || position === ChartLabelPosition.Top

  if (outer) {
    return isHorizontal ? ChartLabelPosition.Right : ChartLabelPosition.Top
  }
  return position
}

const getTitle = (display: ChartDisplay, fallback?: string) =>
  display.title.show
    ? {
        text: display.title.text || fallback || '',
        subtext: display.title.subtext || '',
        left: display.title.left,
        textStyle: { fontSize: display.title.fontSize }
      }
    : undefined

const buildPieOption = (config: ChartConfig, data: ChartData, palette: string[], widgetTitle?: string) => {
  const { display } = config
  const { pie, labels } = display

  const serie = data.series[0]
  const isLegendShown = display.legend.show

  const isRose = pie.variant === ChartPieVariant.RoseRadius || pie.variant === ChartPieVariant.RoseArea
  const innerRadius = pie.variant === ChartPieVariant.Pie ? 0 : pie.innerRadius

  // center and radius are shares of this box, not of the whole chart, so the pie keeps clear of the title and legend
  const legendMargin = getLegendMargin(display, isLegendShown)
  const titleMargin = getTitleMargin(display)

  return {
    color: palette,
    title: getTitle(display, widgetTitle),
    legend: isLegendShown
      ? {
          show: true,
          type: display.legend.type,
          icon: display.legend.icon,
          align: display.legend.align,
          textStyle: { fontSize: display.legend.fontSize },
          ...getLegendPosition(display.legend)
        }
      : { show: false },
    tooltip: {
      show: display.tooltip.show,
      trigger: ChartTooltipTrigger.Item,
      valueFormatter: (value: number) => formatChartNumber(value, display.number, data.isPercent)
    },
    series: [
      {
        type: 'pie',
        name: serie?.name,
        top: titleMargin + legendMargin.top + PIE_GAP,
        right: legendMargin.right + PIE_GAP,
        bottom: legendMargin.bottom + PIE_GAP,
        left: legendMargin.left + PIE_GAP,
        radius: [`${innerRadius}%`, `${pie.outerRadius}%`],
        center: [`${pie.centerX}%`, `${pie.centerY}%`],
        roseType: isRose ? ECHARTS_ROSE_TYPES[pie.variant] : undefined,
        startAngle: pie.startAngle,
        clockwise: pie.clockwise,
        padAngle: pie.padAngle,
        minAngle: pie.minAngle,
        selectedMode: pie.selectedMode === ChartSelectedMode.None ? false : pie.selectedMode,
        avoidLabelOverlap: true,
        itemStyle: { borderRadius: pie.borderRadius, borderColor: '#fff', borderWidth: pie.padAngle ? 0 : 1 },
        label: {
          show: labels.show,
          position:
            labels.position === ChartLabelPosition.Inside ? ChartLabelPosition.Inside : ChartLabelPosition.Outside,
          fontSize: labels.fontSize,
          rotate: labels.rotate,
          formatter: getLabelFormatter(display, data)
        },
        labelLine: { show: pie.labelLine },
        data: data.categories.map((category, index) => ({
          name: category.label,
          value: serie?.values[index],
          ...(category.color && { itemStyle: { color: category.color } })
        }))
      }
    ]
  }
}

const buildAxisOption = (config: ChartConfig, data: ChartData, palette: string[], widgetTitle?: string) => {
  const { display, type } = config
  const { bar, line, labels, xAxis, yAxis } = display

  const isBar = type === ChartType.Bar
  const isHorizontal = isBar && bar.horizontal
  const hasManySeries = data.series.length > 1
  const isLegendShown = display.legend.show && hasManySeries
  const isPercentStack = isBar && bar.stack === ChartStack.Percent && hasManySeries
  const isStacked = hasManySeries && (isBar ? bar.stack !== ChartStack.None : line.stack)
  const isPercent = data.isPercent || isPercentStack
  const colorByCategory = !hasManySeries && (display.colors.byCategory || data.categories.some((item) => item.color))

  const categoryTotals = data.categories.map((_, index) => sumBy(data.series, (serie) => serie.values[index] || 0))

  const legendMargin = getLegendMargin(display, isLegendShown)
  const titleMargin = getTitleMargin(display)

  const categoryAxis = {
    type: 'category',
    show: xAxis.show,
    name: xAxis.name || '',
    nameLocation: xAxis.nameLocation,
    nameGap: xAxis.nameLocation === ChartNameLocation.Middle ? (isHorizontal ? 48 : 32) : 12,
    inverse: isHorizontal ? !xAxis.inverse : xAxis.inverse,
    data: data.categories.map((category) => category.label),
    axisTick: { alignWithLabel: true },
    splitLine: { show: xAxis.splitLine },
    boundaryGap: isBar || !line.area,
    axisLabel: {
      rotate: xAxis.labelRotate,
      interval: xAxis.labelInterval === ChartLabelInterval.All ? 0 : ChartLabelInterval.Auto,
      hideOverlap: xAxis.labelInterval !== ChartLabelInterval.All,
      formatter: (value: string) => truncate(value, xAxis.labelMaxLength)
    }
  }

  const valueAxis = {
    type: yAxis.type,
    show: yAxis.show,
    name: yAxis.name || '',
    nameLocation: yAxis.nameLocation,
    nameGap: yAxis.nameLocation === ChartNameLocation.Middle ? 48 : 12,
    inverse: yAxis.inverse,
    min: isPercentStack ? 0 : (yAxis.min ?? undefined),
    max: isPercentStack ? 100 : (yAxis.max ?? undefined),
    splitLine: { show: yAxis.splitLine },
    axisLabel: { formatter: (value: number) => formatChartNumber(value, { ...display.number, decimals: 0 }, isPercent) }
  }

  const markLineData = [
    ...(display.markLines.average ? [{ type: 'average', name: 'Average' }] : []),
    ...(display.markLines.min ? [{ type: 'min', name: 'Min' }] : []),
    ...(display.markLines.max ? [{ type: 'max', name: 'Max' }] : [])
  ]

  const series = data.series.map((serie) => {
    const values = isPercentStack
      ? serie.values.map((value, index) =>
          value === null || !categoryTotals[index] ? value : (value / categoryTotals[index]) * 100
        )
      : serie.values

    const common = {
      name: serie.name,
      ...(isStacked && { stack: 'total' }),
      ...(serie.color && { itemStyle: { color: serie.color } }),
      emphasis: { focus: hasManySeries ? 'series' : 'none' },
      label: {
        show: labels.show,
        position: getAxisLabelPosition(labels.position, isHorizontal),
        fontSize: labels.fontSize,
        rotate: labels.rotate,
        formatter: getLabelFormatter(display, { ...data, isPercent }, isPercentStack)
      },
      ...(markLineData.length && {
        markLine: {
          data: markLineData,
          label: {
            formatter: (params: any) => `${params.name}: ${formatChartNumber(params.value, display.number, isPercent)}`
          }
        }
      }),
      data: values.map((value, index) => {
        const category = data.categories[index]
        const color = category.color || (colorByCategory ? palette[index % palette.length] : undefined)
        return color ? { value, itemStyle: { color } } : value
      })
    }

    if (isBar) {
      return {
        ...common,
        type: 'bar',
        barWidth: bar.barWidth || undefined,
        barMaxWidth: bar.barMaxWidth,
        barGap: `${bar.barGap}%`,
        showBackground: bar.showBackground,
        itemStyle: { ...common.itemStyle, borderRadius: bar.borderRadius }
      }
    }

    return {
      ...common,
      type: 'line',
      smooth: line.smooth,
      step: line.step === ChartLineStep.None ? false : line.step,
      showSymbol: line.showSymbol,
      symbol: line.symbol,
      symbolSize: line.symbolSize,
      connectNulls: line.connectNulls,
      lineStyle: { width: line.lineWidth },
      ...(line.area && { areaStyle: { opacity: line.areaOpacity } })
    }
  })

  const tooltipFormatter =
    display.tooltip.showTotal && display.tooltip.trigger === ChartTooltipTrigger.Axis && hasManySeries
      ? // ECharts sets the result as HTML: the names are user data (option labels, record titles), so they are escaped
        (params: any[]) => {
          const items = params.map(
            (item) =>
              `${item.marker} ${escape(item.seriesName)}: <b>${formatChartNumber(item.value?.value ?? item.value, display.number, isPercent)}</b>`
          )
          const total = sumBy(params, (item) => Number(item.value?.value ?? item.value) || 0)
          return [
            escape(params[0]?.axisValueLabel),
            ...items,
            `Total: <b>${formatChartNumber(total, display.number, isPercent)}</b>`
          ].join('<br/>')
        }
      : undefined

  return {
    color: palette,
    title: getTitle(display, widgetTitle),
    legend: isLegendShown
      ? {
          show: true,
          type: display.legend.type,
          icon: display.legend.icon,
          align: display.legend.align,
          textStyle: { fontSize: display.legend.fontSize },
          ...getLegendPosition(display.legend)
        }
      : { show: false },
    tooltip: {
      show: display.tooltip.show,
      trigger: display.tooltip.trigger,
      ...(display.tooltip.trigger === ChartTooltipTrigger.Axis && {
        axisPointer: { type: display.tooltip.axisPointer }
      }),
      valueFormatter: (value: number) => formatChartNumber(value, display.number, isPercent),
      ...(tooltipFormatter && { formatter: tooltipFormatter })
    },
    grid: {
      top: display.grid.top + legendMargin.top + titleMargin,
      right: display.grid.right + legendMargin.right,
      bottom: display.grid.bottom + legendMargin.bottom,
      left: display.grid.left + legendMargin.left,
      containLabel: true
    },
    xAxis: isHorizontal ? valueAxis : categoryAxis,
    yAxis: isHorizontal ? categoryAxis : valueAxis,
    series
  }
}

export const buildChartOption = (config: ChartConfig, data: ChartData, widgetTitle?: string) => {
  const palette = getChartPalette(config.display.colors)

  return config.type === ChartType.Pie
    ? buildPieOption(config, data, palette, widgetTitle)
    : buildAxisOption(config, data, palette, widgetTitle)
}
